import React, { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  HomeIcon,
  DocumentTextIcon,
  TableCellsIcon,
  CommandLineIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Home', href: '/', icon: HomeIcon },
  { name: 'Business Requirements Generator', href: '/business-requirement-generator', icon: DocumentTextIcon },
  { name: 'Data Model Generator', href: '/data-model-generator', icon: TableCellsIcon },
  { name: 'SQL Copilot', href: '/sql-copilot', icon: CommandLineIcon },
  { name: 'Detapp', href: '/detapp', icon: RocketLaunchIcon },
];

const navItemClass = (active, collapsed) =>
  [
    'group flex items-center min-h-10 rounded-[10px] text-sm font-medium transition-colors duration-150',
    collapsed ? 'justify-center px-0' : 'px-3 py-2',
    active
      ? 'bg-sidebarActive text-shellText font-semibold'
      : 'text-shellTextMuted hover:bg-shellHover hover:text-shellText',
  ].join(' ');

const NavList = ({ onNavigate, collapsed = false }) => {
  const location = useLocation();
  return (
    <nav className={`pt-6 space-y-[3px] ${collapsed ? 'px-2' : 'px-2.5'}`}>
      {navigation.map((item) => {
        const active = location.pathname === item.href;
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={onNavigate}
            title={collapsed ? item.name : undefined}
            className={navItemClass(active, collapsed)}
          >
            <item.icon
              className={`w-[18px] h-[18px] ${collapsed ? 'flex-shrink-0' : 'min-w-[32px] flex-shrink-0'} ${active ? 'text-shellText' : 'text-shellTextMuted group-hover:text-shellText'}`}
              style={{ strokeWidth: 1.75 }}
              aria-hidden="true"
            />
            {!collapsed && <span className="leading-tight">{item.name}</span>}
          </Link>
        );
      })}
    </nav>
  );
};

const Sidebar = ({ sidebarOpen, setSidebarOpen, desktopSidebarOpen }) => {
  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-sidebarBg">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-1 h-0 sidebar-nav-scroll overflow-y-auto">
                <NavList onNavigate={() => setSidebarOpen(false)} />
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14">{/* Force sidebar to shrink to fit close icon */}</div>
        </Dialog>
      </Transition.Root>

      {/* Persistent desktop sidebar — 260px open, fully hidden when collapsed */}
      <div
        className={`hidden md:block flex-shrink-0 overflow-hidden bg-sidebarBg transition-[width] duration-300 ease-in-out ${
          desktopSidebarOpen ? 'w-[260px]' : 'w-0'
        }`}
      >
        <div className="h-full flex flex-col">
          <nav className="flex-1 sidebar-nav-scroll overflow-y-auto overflow-x-hidden">
            <NavList collapsed={!desktopSidebarOpen} />
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
